import React, { useState, useEffect } from 'react';
import { User, UserRole } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { Plus, Users, Shield, Eye, Save, X } from 'lucide-react';
import './UserManagement.css';

interface UserManagementProps {
  currentUser: User;
  onClose: () => void;
}

interface UserWithId extends User {
  id: string;
}

export function UserManagement({ currentUser, onClose }: UserManagementProps) {
  const { getAllUsers, createUser, clearError } = useAuth();
  const [users, setUsers] = useState<UserWithId[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  
  // Clear any existing errors when component mounts
  useEffect(() => {
    setError(null);
    setSuccess(null);
    clearError(); // Clear AuthContext error as well
  }, [clearError]);
  
  // Create user form state
  const [newUser, setNewUser] = useState({
    email: '',
    password: '',
    displayName: '',
    role: 'viewer' as UserRole
  });

  // Load all users
  const loadUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('Loading users...');
      const usersList = await getAllUsers();
      console.log('Users loaded:', usersList);
      
      const usersWithId = usersList.map(user => ({
        ...user,
        id: user.uid
      }));
      
      setUsers(usersWithId);
    } catch (err: any) {
      console.error('Error loading users:', err);
      if (err.code === 'permission-denied' || /insufficient permissions/i.test(err.message || '')) {
        setError('Permission denied loading the user list. The Firestore security rules must allow admins to read the users collection - deploy the updated firestore.rules.');
      } else {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

      // Create new user
      const handleCreateUser = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!newUser.email || !newUser.password) {
          setError('Email and password are required');
          return;
        }

        try {
          setError(null);

          console.log('Creating user with role:', newUser.role);

          // Use AuthContext createUser function
          await createUser(newUser.email, newUser.password, newUser.role, newUser.displayName);

          console.log('User created successfully, reloading users...');

          // Reset form and reload users
          setNewUser({ email: '', password: '', displayName: '', role: 'viewer' });
          setShowCreateForm(false);
          await loadUsers();

          console.log('Users reloaded');

          // Show success message
          setError(null);
          setSuccess(`User ${newUser.email} created successfully with ${newUser.role} role`);
          
          // Clear success message after 3 seconds
          setTimeout(() => setSuccess(null), 3000);

        } catch (err: any) {
          console.error('Error creating user:', err);
          setError(err.message);
        }
      };



  const getRoleIcon = (role: UserRole) => {
    return role === 'admin' ? Shield : Eye;
  };

  const getRoleColor = (role: UserRole) => {
    return role === 'admin' ? '#e53e3e' : '#3182ce';
  };

  if (currentUser.role !== 'admin') {
    return (
      <div className="user-management">
        <div className="access-denied">
          <Shield className="icon" />
          <h3>Access Denied</h3>
          <p>Only administrators can manage users.</p>
          <button onClick={onClose} className="close-button">Close</button>
        </div>
      </div>
    );
  }

  return (
    <div className="user-management">
      <div>
        <div className="user-management-header">
        <h2>User Management</h2>
        <div className="header-actions">
          <button
            className="create-user-button"
            onClick={() => {
              setShowCreateForm(true);
              setSuccess(null);
              setError(null);
            }}
          >
            <Plus className="icon" />
            Add User
          </button>
          <button onClick={onClose} className="close-button">
            <X className="icon" />
          </button>
        </div>
      </div>

            {error && (
              <div className="error-message">
                {error}
              </div>
            )}

            {success && (
              <div className="success-message">
                {success}
              </div>
            )}

      {showCreateForm && (
        <div className="create-user-form">
          <h3>Create New User</h3>
          <form onSubmit={handleCreateUser}>
            <div className="form-group">
              <label>Email Address</label>
              <input
                type="email"
                value={newUser.email}
                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                required
                placeholder="user@example.com"
              />
            </div>
            
            <div className="form-group">
              <label>Password</label>
              <input
                type="password"
                value={newUser.password}
                onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                required
                placeholder="Minimum 6 characters"
                minLength={6}
              />
            </div>
            
            <div className="form-group">
              <label>Display Name (Optional)</label>
              <input
                type="text"
                value={newUser.displayName}
                onChange={(e) => setNewUser({ ...newUser, displayName: e.target.value })}
                placeholder="John Doe"
              />
            </div>
            
            <div className="form-group">
              <label>Role</label>
              <select
                value={newUser.role}
                onChange={(e) => setNewUser({ ...newUser, role: e.target.value as UserRole })}
              >
                <option value="viewer">Reviewer</option>
                <option value="admin">Administrator</option>
              </select>
            </div>
            
            <div className="form-actions">
              <button type="submit" className="save-button">
                <Save className="icon" />
                Create User
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowCreateForm(false);
                  setSuccess(null);
                  setError(null);
                }}
                className="cancel-button"
              >
                <X className="icon" />
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="users-list">
        <h3>All Users ({users.length})</h3>
        
        {loading ? (
          <div className="loading">Loading users...</div>
        ) : (
          <div className="users-table">
            <div className="table-header">
              <div className="col-user">User</div>
              <div className="col-role">Role</div>
              <div className="col-created">Created</div>
              <div className="col-last-login">Last Login</div>
            </div>
            
            {users.map((user) => {
              const RoleIcon = getRoleIcon(user.role);
              const isCurrentUser = user.uid === currentUser.uid;
              
              return (
                <div key={user.uid} className={`table-row ${isCurrentUser ? 'current-user' : ''}`}>
                  <div className="col-user">
                    <div className="user-info">
                      <div className="user-avatar">
                        <Users className="avatar-icon" />
                      </div>
                      <div className="user-details">
                        <div className="user-name">
                          {user.displayName || user.email?.split('@')[0] || 'Unknown user'}
                          {isCurrentUser && <span className="current-badge">(You)</span>}
                        </div>
                        <div className="user-email" title={user.email || `Auth UID: ${user.uid}`}>
                          {user.email || 'No email on record — will update on next login'}
                        </div>
                      </div>
                    </div>
                  </div>
                  
                        <div className="col-role">
                          <div
                            className="role-badge"
                            style={{ backgroundColor: getRoleColor(user.role) }}
                          >
                            <RoleIcon className="role-icon" />
                            {user.role === 'admin' ? 'Administrator' : 'Reviewer'}
                          </div>
                        </div>
                  
                  <div className="col-created">
                    {user.createdAt.toLocaleDateString()}
                  </div>
                  
                  <div className="col-last-login">
                    {user.lastLogin ? user.lastLogin.toLocaleDateString() : 'Never'}
                  </div>
                  
                </div>
              );
            })}
          </div>
        )}
      </div>
      </div>
    </div>
  );
}
